import { Component, Inject, PLATFORM_ID, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CatalogConfig } from '@app/shared/model/interfaces/catalog';
import { ActivatedRoute, UrlSegment, Router, Scroll } from '@angular/router';
import { isPlatformBrowser, isPlatformServer, ViewportScroller } from '@angular/common';
import { LocaleService } from '@app/core/i18n';
import { select, Store } from '@ngrx/store';
import { Logger } from '@app/core/logging';
import { IAppState } from '@app/store';
import {
  CatalogPage,
  Breadcrumb,
  CatalogFacetLink,
  Campaign,
  SeoContent,
  ContentsMarketingFilter
} from '@vestiaire/api-sdk-angular';
import { RoutingService } from '@app/core/services/routing.service';
import { PageService } from '@app/core/content/services';
import * as fromDataLayer from '@app/analytics/actions/data-layer';
import { SearchIndex } from '@app/shared/components/catalog/services/instantsearch.service';
import { OnCatalogPageLoad, TrackMerchBlock } from '@app/+catalog/store';
import * as fromCatalog from '@app/+catalog/store/reducers';
import * as fromUser from '@app/core/user/store/reducers';
import * as HeaderActions from '@app/core/header/store/actions';
import { combineLatest, Subject } from 'rxjs';
import { ICatalogState } from '@app/+catalog/store/model/catalog-state.model';
import { SearchParameters } from '@app/core/search/common';
import { ServerResponseService } from '@app/shared/services/server-response/server-response.service';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ISearchState } from '@app/shared/model/interfaces/store/search-state';
import { campaignMatcher, directShippingMatcher, expressDeliveryMatcher } from './catalog-page-routing.module';
import { RefinementsService } from '@app/shared/components/catalog/services/refinements.service';
import { PageIndexation } from '@app/core/content/models';
import { IndexSortBy } from '@app/shared/components/catalog/custom-widgets/custom-sort-by-selector/index-sort-by';
import { ContentsRouteParameter } from '@vestiaire/api-sdk-angular';
import { CatalogLinksResolverResponse } from './catalog-links-resolver.service';
import { SeoContentsService } from '@vestiaire/api-sdk-angular';
import { defaultMetas } from '@app/shared/static/metas';
import { MarketingFilterService } from './service/marketing-filter.service';
import { IsLoggedGuard } from '@app/shared/guards';

export enum FacetTypes {
  campaigns = 'campaigns',
  directShipping = 'dsEligibleCountries',
  expressDelivery = 'stock'
}

@Component({
  selector: 'catalog-page',
  templateUrl: './catalog-page.component.html',
  styleUrls: ['./catalog-page.component.scss']
})
export class CatalogPageComponent implements OnInit, OnDestroy, AfterViewInit {
  private translationKey: string;
  private ngUnsubscribe = new Subject<void>();
  private campaignId = '';
  private filteredCampaignId = '';

  page: CatalogPage | null;
  configCatalog: CatalogConfig = {
    indexName: SearchIndex.Default,
    withFilters: true,
    withPagination: true,
    hitsPerPage: 48
  };
  searchParameters: SearchParameters = {};
  breadcrumb: Array<Breadcrumb> = [];
  likedItem: string[];
  facetLink: CatalogFacetLink | null;
  seoLinks: CatalogLinksResolverResponse;
  seoContents: SeoContent | null;
  nbPages: number;
  nbProducts: number;
  pageNumber = 1;
  crawlPagination: false;
  hasLessThanFourProducts = false;
  searchStore: ISearchState;
  currentCountry: string;
  campaign: Campaign;
  marketingFilter: ContentsMarketingFilter[];

  scrollPosition: [number, number] = [0, 0];

  constructor(
    private route: ActivatedRoute,
    private localeService: LocaleService,
    private pageService: PageService,
    private routingService: RoutingService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private store: Store<IAppState>,
    private logger: Logger,
    private serverResponseService: ServerResponseService,
    private viewportScroller: ViewportScroller,
    private router: Router,
    private refinementsService: RefinementsService,
    private seoContentService: SeoContentsService,
    private marketingFilterService: MarketingFilterService,
    private loggedGuard: IsLoggedGuard
  ) {
    this.router.events.pipe(filter(e => e instanceof Scroll)).subscribe(
      (e: Scroll) => {
        if (e.position) {
          this.scrollPosition = e.position;
        } else {
          this.scrollPosition = [0, 0];
        }
      },
      err => this.logger.error('Could not subscribe to router events', err)
    );

    // Force to reload component when navigating to the same route but with new params : /c/campaign-2/ -> /c/campaigns-2/#gender=women...
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit() {
    this.translationKey = this.localeService.getTranslationKey();
    this.refinementsService.getSortByValue().subscribe(
      ({ name }) => (this.configCatalog.indexName = name),
      error => null
    );

    this.addCatalogPageSubscriber();
    this.addUrlSubscriber();
    this.addDataSubscriber();
    this.addQueryParamsSubscribers();
    this.addLikedItemSubscriber();
    this.getCurrentCountry();
    this.checkPersonalization();
    this.store.pipe(select('search')).subscribe(
      res => {
        this.searchStore = res;
      },
      error => {
        throw error;
      }
    );
  }

  ngAfterViewInit() {
    this.viewportScroller.scrollToPosition(this.scrollPosition);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.store.dispatch(
      new fromDataLayer.Persist({ index_name: '', onsite_campaign_id: '', filtered_campaign_id: '' })
    );
  }

  navigate(segments: UrlSegment[]): void {
    let url = this.routingService.buildPath(segments);

    const match = this.routingService.getCatalogPaginationMatch(url);
    url = match ? match[1] : url;
    const page = match ? Number(match[2]) : 0;

    let ruleContexts = this.getUrlContext(url);
    let facet = 'catalogLinksWithoutLanguage';

    const newItemsUrl = ['new-items', 'nouveautes', 'neuheiten', 'novita', 'novedades'];
    if (newItemsUrl.includes(segments[0].path)) {
      this.refinementsService.setSortByValue({
        index: IndexSortBy.Recent,
        name: SearchIndex.Recent
      });
    }

    if (campaignMatcher(segments)) {
      facet = FacetTypes.campaigns;
      url = this.routingService.getCampaignId(segments[1].path);
      this.campaignId = this.routingService.getCampaignId(segments[1].path);
      ruleContexts = [''];
    } else if (directShippingMatcher(segments)) {
      facet = FacetTypes.directShipping;
      url = this.localeService.getCountry();
      ruleContexts = [''];
      this.campaignId = '';
    } else if (expressDeliveryMatcher(segments)) {
      facet = FacetTypes.expressDelivery;
      url = '1';
      ruleContexts = [''];
      this.campaignId = '';
    } else {
      this.campaignId = '';
    }
    ruleContexts.push(this.localeService.getCountry());
    this.store.dispatch(
      new fromDataLayer.Persist({
        onsite_campaign_id: this.campaignId,
        filtered_campaign_id: this.filteredCampaignId
      })
    );

    this.searchParameters = {
      facets: [facet],
      facetsRefinements: { [facet]: [url] },
      ruleContexts
    };

    if (page > 0) {
      this.searchParameters.page = page - 1;
    }

    this.logger.debug(`Navigate to ${url}`, this.searchParameters);

    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }

  search(query: string): void {
    // reset context(merch blockId) passed to store and filtered_campaign_id varaible in data-layer on search
    this.store.dispatch(new TrackMerchBlock({ cmsBlockId: '', isMerchBlock: false }));
    this.store.dispatch(new fromDataLayer.Persist({ filtered_campaign_id: '' }));

    this.searchParameters = { query };
    this.logger.debug(`Search '${query}'`, this.searchParameters);
    if (
      this.searchParameters.query &&
      this.searchParameters.query.length > 0 &&
      defaultMetas[this.localeService.getLanguage()] &&
      defaultMetas[this.localeService.getLanguage()].searchTitle &&
      defaultMetas[this.localeService.getLanguage()].searchDescription
    ) {
      this.seoContentService
        .getSearchSeoContent(this.searchParameters.query, this.localeService.getLanguage())
        .subscribe(
          resultsSeoContent => (this.seoContents = resultsSeoContent.data),
          err => this.logger.error('Could not subscribe to search changes', err)
        );
      this.pageService.setTitle(
        defaultMetas[this.localeService.getLanguage()].searchTitle.replace(
          'searchValue',
          this.searchParameters.query.replace(/[A-zÀ-ÿ0-9_-]\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1))
        )
      );
      this.pageService.setDescription(
        defaultMetas[this.localeService.getLanguage()].searchDescription.replace(
          'searchValue',
          this.searchParameters.query.replace(/[A-zÀ-ÿ0-9_-]\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1))
        )
      );
      this.pageService.removeKeywords();
      if (this.router.url && this.router.url.length > 0) {
        this.pageService.setCanonicalVersion(this.router.url.toLowerCase());
      }
    }
  }

  private updatePageData(page: CatalogPage): void {
    this.pageService.setTitle(String(page.metadata.title));
    if (page.metadata.description) {
      this.pageService.setDescription(String(page.metadata.description));
    } else {
      this.pageService.setDescription('');
    }

    if (page.content.headline) {
      this.pageService.setKeywords(page.content.headline);
    }
    if (!isPlatformBrowser(this.platformId)) {
      this.customMetaData();
    }
  }

  private customMetaData() {
    let title = String(this.page.metadata.title);
    let description = this.page.metadata.description || '';
    let canonicalUrl = this.page.path;

    this.pageService.clearLanguageVersions();

    if (this.isFirstPage) {
      this.pageService.setLanguageVersionsWithContentsLocalizedPath(this.page.alternateVersions);
    } else {
      title = `${title} - Page ${this.pageNumber}`;
      if (this.page.metadata.description) {
        description = `${description} (${this.pageNumber})`;
        this.pageService.setDescription(description);
      }
      if (this.page.indexation.crawlPagination) {
        canonicalUrl += `p-${this.pageNumber}/`;
      }
    }

    this.pageService.setTitle(title);
    this.pageService.setRobots(this.pageIndexation);
    this.pageService.setCanonicalVersion(canonicalUrl);
  }

  /**
   * define index robot according to facet and universe
   */
  private isFacetsUniverseIndexed(index: boolean): boolean {
    const { IdColors, IdMaterials, IdUniverse } = ContentsRouteParameter.NameEnum;
    const facetsToHideByUniverse = {
      3: [IdColors, IdMaterials],
      4: [IdColors, IdMaterials]
    };
    const universe = this.page.route.parameters.find(param => param.name === IdUniverse);

    if (!universe || !facetsToHideByUniverse[universe.value]) {
      return index;
    }

    for (const facet of facetsToHideByUniverse[universe.value]) {
      if (this.page.route.parameters.find(param => param.name === facet)) {
        return false;
      }
    }

    return index;
  }

  get pageIndexation(): PageIndexation {
    let index = this.isFacetsUniverseIndexed(this.page.indexation.index);

    if (index) {
      if (this.nbProducts !== null && (this.lastPageHaveLessThanFourProduct || this.isNoWhiteListedAndPagination)) {
        index = false;
      } else if (this.page.path.match('^/we-love/.+') != null) {
        index = false;
      }
    }

    return { index, follow: true };
  }

  get lastPageHaveLessThanFourProduct(): boolean {
    return this.isLastPage && this.nbProducts - (this.pageNumber - 1) * 60 <= 3;
  }

  get isNoWhiteListedAndPagination(): boolean {
    return !this.isFirstPage && !this.page.isOpenedForPagination;
  }

  get isFirstPage(): boolean {
    return this.pageNumber === 1;
  }

  get isLastPage(): boolean {
    return this.pageNumber >= this.nbPages;
  }

  private getUrlContext(url: string): string[] {
    const urlContext = url
      .replace(/\/\.?$/, '')
      .replace(/^\//, '')
      .replace(/[^\/a-zA-Z0-9_-]/, '-')
      .replace(/\//g, '_');

    return [`url_${this.translationKey}_${urlContext}`];
  }

  private addUrlSubscriber(): void {
    this.route.url.subscribe(
      url => {
        if (url.length > 0 && url[0].path !== 'search') {
          this.navigate(url);
        }
      },
      err => this.logger.error('Could not subscribe to url changes', err)
    );
  }

  private addDataSubscriber(): void {
    this.route.data.subscribe(
      data => {
        const pageData = { pageType: 'catalog', name: '' };

        if (data.campaign) {
          this.campaign = data.campaign as Campaign;
          this.pageService.setTitle(`${this.campaign.localizedTitle || this.campaign.title} - Vestiaire Collective`);
          this.pageService.setDescription(this.campaign.localizedDescription || this.campaign.description);
        }

        if (data.breadcrumb) {
          this.breadcrumb = data.breadcrumb;
        }

        if (data.pageAnalytics) {
          this.store.dispatch(new fromDataLayer.Persist(data.pageAnalytics));
        }

        if (data.pageData) {
          this.page = data.pageData;
          this.updatePageData(data.pageData);

          this.crawlPagination = data.pageData.indexation.crawlPagination;

          pageData.name = data.pageData['route']['name'];
          data.pageData['route']['parameters'].map(param => (pageData[param['name']] = param['value']));
        }

        if (this.page) {
          this.store.dispatch(new OnCatalogPageLoad(this.page));
        }

        if (data.catalogLinks && data.catalogLinks.getFacetLinks) {
          this.facetLink = data.catalogLinks.getFacetLinks;
        }
        if (data.catalogLinks && data.catalogLinks.listSeoLinksCatalog) {
          this.seoLinks = data.catalogLinks.listSeoLinksCatalog;
        }

        this.marketingFilterService.getMarketingFilter().subscribe(
          res => {
            this.marketingFilter = res;
          },
          error => {
            console.error('Marketing Filter Error', error);
          }
        );

        this.store.dispatch(new HeaderActions.SetPageParams(pageData));
      },
      err => this.logger.error('Could not subscribe to route data changes', err)
    );
  }

  private addQueryParamsSubscribers(): void {
    // retrieve ?q=query used in default search
    const queryParamsQuery$ = this.route.queryParams.pipe(map(queryParams => queryParams.q));
    // retrieve /s/:query used in seo custom trololo search
    const routeQuery$ = this.route.params.pipe(map(params => params.query));

    combineLatest([queryParamsQuery$, routeQuery$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        ([q, query]) => {
          if (q) {
            this.search(q);
          } else if (query) {
            // https://vestiairecollective.atlassian.net/browse/PAAS-562?focusedCommentId=337965
            const parsedQuery = query.replace('-', ' ');
            this.search(parsedQuery);
          }
        },
        err => this.logger.error('Could not subscribe to query params changes', err)
      );
  }

  addCatalogPageSubscriber() {
    this.store.pipe(select(fromCatalog.getState), takeUntil(this.ngUnsubscribe)).subscribe(
      (data: ICatalogState) => {
        if (data) {
          this.pageNumber = data.pageNumber ? data.pageNumber : 1;
          this.nbPages = data.nbPages;
          this.filteredCampaignId = data.merchBlockInfo?.isMerchBlock ? data.merchBlockInfo.cmsBlockId : '';
          if (isPlatformServer(this.platformId)) {
            if (this.page && this.nbPages && this.nbPages < this.pageNumber && this.crawlPagination) {
              this.serverResponseService.redirect(301, this.page.path);
              return;
            }

            this.hasLessThanFourProducts = data.nbProducts !== null && data.nbProducts < 4;
            this.nbProducts = data.nbProducts;
          }
        }

        if (this.page) {
          this.customMetaData();
          this.addPaginationLinks();
        }
      },
      err => {}
    );
  }

  addLikedItemSubscriber() {
    this.store.pipe(select(fromUser.getLikedItems), takeUntil(this.ngUnsubscribe)).subscribe(
      productsLiked => {
        if (productsLiked) {
          this.likedItem = productsLiked['products'];
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  addPaginationLinks() {
    if (this.crawlPagination) {
      let prevPageUrl = this.page.path;
      if (this.pageNumber > 2) {
        prevPageUrl += 'p-' + (this.pageNumber - 1) + '/';
      }
      const nextPageUrl = this.page.path + 'p-' + (this.pageNumber + 1) + '/';

      this.pageService.setPaginationLink('prev', this.isFirstPage ? null : prevPageUrl);
      this.pageService.setPaginationLink('next', this.isLastPage ? null : nextPageUrl);
    }
  }

  getCurrentCountry() {
    this.currentCountry = this.localeService.getCountry();
  }

  checkPersonalization() {
    // User should be connected to open personalization
    if (this.route.snapshot?.queryParams?.openPersonalization) {
      this.loggedGuard.isLoggedIn();
    }
  }
}
